// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import * as THREE from 'three';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import fontJson from 'three/examples/fonts/helvetiker_regular.typeface.json';
import { Canvas } from '@react-three/fiber';
import { toast } from 'react-toastify';
import clsx from 'clsx';

// Internal Dependencies
import MainMap from './MainMap';
import MallNavigationForm from './MallNavigationForm';
import EdgeForm from './EdgeForm';
import ManageWaypointBanner from './ManageWaypointBanner';
import { useMallMapStore } from '../../../hook/useMallMapStore';
import Constants from '../../../common/constants';
import { IsEmpty, IsTrue } from '../../../common/checks';
import { calculateEdgeInitialCost } from '../../../common/util';
import { getPagesByParams } from '../../../actions/admin/pageAction';
import apiService from '../../../services.js/apiService';
import Colours from '../../../styles/colours';

const useStyles = makeStyles(() => ({
  levelContainer: {
    display:         'inline-block',
    border:          `1px solid ${Colours.Black}`,
    position:        'absolute',
    top:             '15%',
    left:            '2%',
    zIndex:          10,
    backgroundColor: Colours.White,
  },
  levelText: {
    margin:    '8px',
    padding:   '8px 10px',
    fontSize:  '16px',
    textAlign: 'center',
    cursor:    'pointer',
  },
  levelSelected: {
    border: `1px solid ${Colours.Black}`,
  },
  formContainer: {
    position:        'absolute',
    padding:         '25px',
    backgroundColor: Colours.White,
    width:           '340px',
    top:             '10%',
    right:           '1%',
  },
}));

const { MANAGE_WAYPOINTS, MANAGE_LINKS } = Constants.MallMapManageButtons;
const zAxis = 0;
let initialWaypointEdgesRender = true;
const labelMaterial = new THREE.MeshBasicMaterial({ color: 'black' });

const MallNavigationMap = ({ mallMapData, mallDirectoryList, setMallMapData, setIsLoading }) => {
  const classes = useStyles();
  const {
    selectedButton,
    selectedWaypoint,
    setSelectedWaypoint,
    isEdgeViewMode,
    showForm,
    font,
    setFont,
    selectedWaypointLabel,
    isWaypointTextVisible,
  } = useMallMapStore((state) => state);

  const [waypointEdges, setWaypointEdges] = useState([]);
  const [waypointLabels, setWaypointLabels] = useState([]);
  const [currentLevel, setCurrentLevel] = useState(mallMapData[0].level);
  const [pageList, setPageList] = useState([]);
  const [connectionWaypointEdges, setConnectionWaypointEdges] = useState([]);
  const [waypointMapPosY, setWaypointMapPosY] = useState(0);
  const [isWaypointTextChanged, setIsWaypointTextChanged] = useState(false);

  const changeLevelHandler = (level) => {
    setCurrentLevel(level);
  };

  // EDGES
  const getWaypointEdges = (waypoint) => {
    const currentWaypointEdges = [];

    waypointEdges.forEach((edge) => {
      if ((edge.waypointAId === waypoint.id || edge.waypointBId === waypoint.id) && !currentWaypointEdges.some((entry) => entry.id === edge.id)) {
        currentWaypointEdges.push(edge);
      }
    });

    return currentWaypointEdges;
  };

  const generateEdgePoint = (waypointA, waypointB) => {
    const points = [];
    if (waypointA && waypointB) {
      points.push(new THREE.Vector3(waypointA.x, waypointA.y, zAxis));
      points.push(new THREE.Vector3(waypointB.x, waypointB.y, zAxis));
    }
    return points;
  };

  const onAddNewEdge = async (waypointA, waypointB, currentLevelIndex) => {
    try {
      const { pageId } = mallMapData[currentLevelIndex];
      const edgeTravelCost = calculateEdgeInitialCost(waypointA.type, waypointB.type);

      const newEdge = {
        geometry:        new THREE.BufferGeometry().setFromPoints(generateEdgePoint(waypointA, waypointB)),
        waypointA,
        waypointAId:     waypointA.id,
        waypointB,
        waypointBId:     waypointB.id,
        cost:            edgeTravelCost,
        isBidirectional: true,
        isHighlight:     false,
        pageId,
      };

      const addedEdge = await apiService.createEdge(newEdge);
      newEdge.id = addedEdge.id;

      setWaypointEdges([...waypointEdges, newEdge]);
    } catch (err) {
      console.log('Something went wrong while adding a new edge');
      console.log(err);
    }
  };

  const onAddWaypointEdge = async (waypointsEdge, currentLevelIndex) => {
    try {
      if (isEdgeViewMode) {
        return;
      }

      const connectionsWaypointTypeEdges = _.map(waypointsEdge, (waypoint) => waypoint.type);
      const waypointTypeForEdgeBetweenLevels = [
        Constants.WaypointType.Lift,
        Constants.WaypointType.Escalator,
        Constants.WaypointType.Staircase,
      ];

      if (
        waypointsEdge[0].mallMapId !== waypointsEdge[1].mallMapId
        && !connectionsWaypointTypeEdges.every((edge) => waypointTypeForEdgeBetweenLevels.includes(edge))
      ) { // if not same mall
        toast.warn('Please use waypoint "Escalator", "Staircase", "Lift" to connect to other mall level');
        return true;
      }

      let existId = null; // Check the edge is exist/not

      for (let index = 0; index < waypointEdges.length; index += 1) {
        const thisEdgesConnection = `${waypointEdges[index].waypointAId}-${waypointEdges[index].waypointBId}`;
        if (thisEdgesConnection === `${waypointsEdge[0].id}-${waypointsEdge[1].id}` || thisEdgesConnection === `${waypointsEdge[1].id}-${waypointsEdge[0].id}`) {
          existId = waypointEdges[index].id;
          break;
        }
      }

      // Add new waypoints edge
      if (IsEmpty(existId)) {
        onAddNewEdge(waypointsEdge[0], waypointsEdge[1], currentLevelIndex);
      }
    } catch (err) {
      toast.error('Something went wrong while adding waypoint edge');
      console.log(err);
    }
  };

  const manageWaypointHandler = async (e, waypoint, mapPosY) => {
    try {
      // Update position
      const { x: newX, z } = e.point;
      const newY = z * -1;
      const copyMallMapData = _.cloneDeep(mallMapData);
      const currentLevelIndex = _.findIndex(copyMallMapData, (map) => map.id === waypoint.mallMapId);

      const xDiff = Math.floor(waypoint.x.toFixed(1)) - Math.floor(newX.toFixed(1));
      const yDiff = Math.floor(waypoint.y.toFixed(1)) - Math.floor(newY.toFixed(1));

      if ((xDiff === 0) && (yDiff === 0)) {
        setSelectedWaypoint(waypoint);
      } else {
        waypoint.x = newX;
        waypoint.y = newY;

        copyMallMapData[currentLevelIndex].waypoints = _.map(copyMallMapData[currentLevelIndex].waypoints, (wp) => {
          if (wp.id === waypoint.id) {
            wp.x = waypoint.x;
            wp.y = waypoint.y;
          }

          return wp;
        });

        await apiService.updateWaypoint(waypoint.id, { ...waypoint,
          y: waypoint.y < 0 ? waypoint.y + mapPosY : waypoint.y - mapPosY,
        });

        setMallMapData(copyMallMapData);
        setSelectedWaypoint(waypoint);
      }
    } catch (err) {
      toast.error('Something went wrong while clicking waypoint');
      console.log(err);
    }
  };

  const manageLinkHandler = async (e, waypoint) => {
    try {
      // Update position
      let hasError = false;
      const { x: newX, z } = e.point;
      const newY = z * -1;
      const copyMallMapData = _.cloneDeep(mallMapData);
      const currentLevelIndex = _.findIndex(copyMallMapData, (map) => map.id === waypoint.mallMapId);

      if (!IsEmpty(selectedWaypoint) && selectedWaypoint.id === waypoint.id) {
        setSelectedWaypoint(null);
        setConnectionWaypointEdges([]);
        return;
      }

      // Draw Edge
      if (selectedButton === MANAGE_LINKS) {
        switch (connectionWaypointEdges.length) {
          case 0:
            setConnectionWaypointEdges([waypoint]);
            break;
          case 1:
            if (waypoint.id !== connectionWaypointEdges[0].id) {
              hasError = (await onAddWaypointEdge([...connectionWaypointEdges, waypoint], currentLevelIndex) || false);

              if (!IsTrue(hasError)) {
                setConnectionWaypointEdges([waypoint]);
              }
            }
            break;
          default:
            setConnectionWaypointEdges(waypoint);
        }
      }

      const xDiff = Math.floor(waypoint.x.toFixed(1)) - Math.floor(newX.toFixed(1));
      const yDiff = Math.floor(waypoint.y.toFixed(1)) - Math.floor(newY.toFixed(1));

      if ((xDiff >= 0 && xDiff <= 1) && (yDiff >= 0 && yDiff <= 1)) {
        if (!IsTrue(hasError)) {
          setSelectedWaypoint(waypoint);
        }
      }
    } catch (err) {
      toast.error('Something went wrong while clicking waypoint');
      console.log(err);
    }
  };

  // WAYPOINTS
  const onClickWaypoint = async (e, waypoint, mapPosY) => {
    try {
      setWaypointMapPosY(mapPosY);
      if (selectedButton === MANAGE_WAYPOINTS) { // waypoint
        manageWaypointHandler(e, waypoint, mapPosY);
      } else if (selectedButton === MANAGE_LINKS) { // edge
        manageLinkHandler(e, waypoint);
      }
    } catch (err) {
      toast.error('Something went wrong while clicking waypoint');
      console.log(err);
    }
  };

  /**
   * Debouncing the "onAddWaypoint", in order to avoid multiple waypoints being added at the same time
   */
  const onAddWaypoint = _.debounce(async (waypoint, posY) => {
    try {
      const copyMallMapData = _.cloneDeep(mallMapData);
      const currentLevelIndex = _.findIndex(copyMallMapData, (map) => map.id === waypoint.mallMapId);

      const newWaypoint = await apiService.createWaypoint(waypoint);
      newWaypoint.y -= posY;
      copyMallMapData[currentLevelIndex].waypoints = [...copyMallMapData[currentLevelIndex].waypoints, newWaypoint];

      setMallMapData(copyMallMapData);
    } catch (err) {
      toast.error(err);
    }
  }, 200);

  const onDeleteWaypoint = async (waypoint) => {
    try {
      let copyWaypointEdges = _.cloneDeep(waypointEdges);
      const copyMallMapData = _.cloneDeep(mallMapData);
      const currentLevelIndex = _.findIndex(copyMallMapData, (map) => map.id === waypoint.mallMapId);
      const filteredWaypoints = _.filter(copyMallMapData[currentLevelIndex].waypoints, (wp) => wp.id !== waypoint.id);
      copyMallMapData[currentLevelIndex].waypoints = filteredWaypoints;

      const edges =  getWaypointEdges(waypoint);

      if (!IsEmpty(edges)) {
        copyWaypointEdges = _.filter(copyWaypointEdges, (edge) => !edges.some((e) => e.id === edge.id));
      }

      await apiService.deleteWaypoint(waypoint.id);

      setMallMapData(copyMallMapData);
      setWaypointEdges(copyWaypointEdges);
      setSelectedWaypoint(null);
    } catch (err) {
      toast.error(err);
    }
  };

  const onUpdateWaypoint = async (id, waypoint, isTextChanged = false) => {
    try {
      const copyMallMapData = _.cloneDeep(mallMapData);
      const currentLevelIndex = _.findIndex(copyMallMapData, (map) => map.id === selectedWaypoint.mallMapId);

      copyMallMapData[currentLevelIndex].waypoints = _.map(copyMallMapData[currentLevelIndex].waypoints, (wp) => {
        if (wp.id === id) {
          wp = waypoint;
        }

        return wp;
      });

      await apiService.updateWaypoint(id, { ...waypoint,
        y: waypoint.y < 0 ? waypoint.y + waypointMapPosY : waypoint.y - waypointMapPosY,
      });

      setSelectedWaypoint(waypoint);
      setMallMapData(copyMallMapData);
      setIsWaypointTextChanged(isTextChanged);
    } catch (err) {
      toast.error(err);
      setIsLoading(false);
    }
  };

  const generateLabel = (text) => {
    const labelGeometry = new TextGeometry(text, { font, size: 0.2, height: 0 }).center();
    const labelMesh = new THREE.Mesh(labelGeometry, labelMaterial);

    return labelMesh;
  };

  const generateLabels =  () => {
    // Delete TextGeometry, when waypointText is not visible
    if (!isWaypointTextVisible) {
      _.forEach(waypointLabels, (label) => {
        label.object.geometry.dispose();
        label.object.material.dispose();
      });

      setWaypointLabels([]);
      return;
    }

    let labels = [];

    if (IsEmpty(waypointLabels)) { // Generated at the time, when "isWaypointTextVisible" is true
      _.forEach(mallMapData, (mallMap, index) => {
        _.forEach(mallMap.waypoints, (waypoint) => {
          if (!IsEmpty(waypoint.name)) {
            const zValue = index === 0 ? 2 : 2 + ((index + 1) ** 2) - index;
            // Label
            const labelMesh = generateLabel(waypoint.name);
            labelMesh.position.set(waypoint.x, waypoint.y, zValue);
            labelMesh.rotation.z = waypoint.textRotation * (Math.PI / 180);

            labels.push({
              key:    `waypoint-label-${waypoint.id}`,
              object: labelMesh,
              waypoint,
            });
          }
        });
      });

      setWaypointLabels(labels);
    } else if (!IsEmpty(selectedWaypointLabel)) {
      // Increase performance, rather than creating the textGeometry again, we only update the "rotation" value on them
      labels = _.map(waypointLabels, (label) => {
        if (label.key === `waypoint-label-${selectedWaypointLabel.id}`) {
          label.object.rotation.z = selectedWaypointLabel.textRotation * (Math.PI / 180);
        }

        return label;
      });

      setWaypointLabels(labels);
    } else if (!IsEmpty(selectedWaypoint)) {
      const currentLevelIndex = _.findIndex(mallMapData, (map) => map.id === selectedWaypoint.mallMapId);
      const zValue = currentLevelIndex === 0 ? 2 : 2 + ((currentLevelIndex + 1) ** 2) - currentLevelIndex;

      let waypointLabelFound = false;
      let waypointLabelChanged = isWaypointTextChanged;

      // Increase performance, rather than creating the textGeometry again, we only update the "position" value on them
       _.forEach(waypointLabels, (label) => {
         if (label.key === `waypoint-label-${selectedWaypoint.id}`) {
           if (waypointLabelChanged) {
             // Remove the old textGeometry
             label.object.geometry.dispose();
             label.object.material.dispose();

             // create a new textGeometry
             label.object = generateLabel(selectedWaypoint.name);
             label.object.position.set(selectedWaypoint.x, selectedWaypoint.y, zValue);
             label.object.rotation.z = selectedWaypoint.textRotation * (Math.PI / 180);

             waypointLabelChanged = false;
             waypointLabelFound = true;
           } else {
             label.object.position.x = selectedWaypoint.x;
             label.object.position.y = selectedWaypoint.y;
           }
         }

         labels.push(label);
       });

       // Add new waypointLabel in state "waypointLabels"
       if (waypointLabelChanged && !waypointLabelFound) {
         const labelMesh = generateLabel(selectedWaypoint.name);
        labelMesh.position.set(selectedWaypoint.x, selectedWaypoint.y, zValue);
        labelMesh.rotation.z = selectedWaypoint.textRotation * (Math.PI / 180);

        labels.push({
          key:      `waypoint-label-${selectedWaypoint.id}`,
          object:   labelMesh,
          waypoint: selectedWaypoint,
        });
       }

      setWaypointLabels(labels);

      if (isWaypointTextChanged) {
        setIsWaypointTextChanged(false);
      }
    }
  };

  const fetchPageDataHandler = async () => {
    try {
      setIsLoading(true);

      // Get list of pages
      const pages = await getPagesByParams({
        types:    [Constants.PageType.Retailer, Constants.PageType.FNB],
        status:   Constants.PageStatus.Live, pageSize: 10000,
      })();

      if (!IsEmpty(pages)) {
        const formattedPages = _.map(pages.rows, (p) => ({ ...p, value: p.name, label: p.name }));
          setPageList(formattedPages);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (IsEmpty(font)) {
      setFont(new FontLoader().parse(fontJson));
    }

    setWaypointEdges(mallMapData[0].edges);
    fetchPageDataHandler();
  }, []);

  useEffect(() => {
    if (!IsEmpty(font)) {
      generateLabels();
    }
  }, [font, isWaypointTextVisible, selectedWaypointLabel, mallMapData]);

  useEffect(() => {
    if (initialWaypointEdgesRender) {
      setWaypointEdges(mallMapData[0].edges);
      initialWaypointEdgesRender = false;
    } else {
      const copyMallMapData = _.cloneDeep(mallMapData);

      copyMallMapData.forEach((mallMap) => {
        mallMap.edges = waypointEdges;
      });

      setMallMapData(copyMallMapData);
    }
  }, [waypointEdges]);

  return (
    <div className="position-relative">
      <div className="position-absolute top-0 w-100 d-flex align-items-center justify-content-between py-2 px-4" style={{ zIndex: 100 }}>
        {selectedButton === MANAGE_WAYPOINTS && <ManageWaypointBanner />}
      </div>

      <div className={classes.levelContainer}>
        <Typography className={classes.levelText}>Lvl</Typography>
        {_.map(mallMapData, (map, index) => (
          <Typography
            key={map.id}
            className={clsx([classes.levelText, currentLevel === map.level && classes.levelSelected])}
            onClick={() => changeLevelHandler(map.level, index)}
          >
            {map.level}
          </Typography>
        ))}
      </div>

      <Canvas style={{ height: '80vh', background: Colours.Gray }}>
        <MainMap
          mallMapData={mallMapData}
          waypointEdges={waypointEdges}
          waypointLabels={waypointLabels}
          onClickWaypoint={onClickWaypoint}
          onAddWaypoint={onAddWaypoint}
          setCurrentLevel={setCurrentLevel}
        />
      </Canvas>

      { !IsEmpty(selectedWaypoint) && showForm && (
        (selectedButton === MANAGE_WAYPOINTS)
          ? (
            <div className={classes.formContainer}>
              <MallNavigationForm
                selectedWaypoint={selectedWaypoint}
                pageListOptions={pageList}
                mallDirectoryListOptions={mallDirectoryList}
                onDeleteWaypoint={onDeleteWaypoint}
                onUpdateWaypoint={onUpdateWaypoint}
              />
            </div>
          )
          : (selectedButton === MANAGE_LINKS) ? (
            <div className={classes.formContainer}>
              <EdgeForm waypointEdges={waypointEdges} setWaypointEdges={setWaypointEdges} />
            </div>
          ) : null
      )}
    </div>
  );
};

MallNavigationMap.propTypes = {
  mallMapData:       PropTypes.arrayOf(PropTypes.shape(Object)),
  mallDirectoryList: PropTypes.arrayOf(PropTypes.shape(Object)),
  setMallMapData:    PropTypes.func,
  setIsLoading:      PropTypes.func,
};

MallNavigationMap.defaultProps = {
  mallMapData:       [],
  mallDirectoryList: [],
  setMallMapData:    () => {},
  setIsLoading:      () => {},
};

export default MallNavigationMap;
